import type Badge from "@travily/types/models/BadgesRow";
import { cx } from "@ui/utils/cx";
import { useEffect, useState } from "react";
import { BadgeHoverCard } from "@/components/PassportBadges/BadgeHoverCard";
import { BadgeImage, passportSlot } from "@/components/PassportBadges/PassportSlot";
import { filterBadgesWithArtwork, resolveBadgeImage } from "@/images/badges";
import passportPlaceholders from "@/images/passport/passport-mobile-placeholders.webp";
import passportRings from "@/images/passport/passport-mobile-stretch1.webp";
import passportPaper from "@/images/passport/passport-mobile-stretch2.webp";
import passportBottom from "@/images/passport/passport-mobile-stretch3.webp";
import { CurvedSectionTitle } from "./CurvedSectionTitle";
import { PassportCloseButton } from "./PassportCloseButton";
import { paginateBySection } from "./paginate-by-section";

// The mobile notepad scrolls vertically: a spiral-ring header that scrolls away
// (`stretch1`), a paper body stretched to whatever height N badges need
// (`stretch2`), and a bottom paper edge (`stretch3`). It mirrors the desktop book:
// the placeholder (`...-placeholders.webp`) is a fixed 2×3 *tile* of arches — the
// notepad stacks one tile per section page (six badges). Per-slot boxes are % of
// the tile, measured from the alpha bbox of each arch (NOT eyeballed); badges and
// the empty-slot dashed arches both derive from these, so a badge always lands on
// its slot and reads the same size as an empty one — exactly like desktop.
const TILE_SLOTS: Array<{ left: number; top: number; width: number }> = [
  { left: 0.15, top: 0.09, width: 46.51 },
  { left: 54.09, top: 0.47, width: 45.91 },
  { left: 0.15, top: 34.94, width: 45.77 },
  { left: 53.34, top: 35.89, width: 46.66 },
  { left: 0.15, top: 70.64, width: 46.66 },
  { left: 54.23, top: 71.12, width: 45.77 },
];

const TILE_W = 673;
const TILE_H = 1056;
const INNER_WIDTH = "65.8%"; // badge area inset from the paper edges (measured)

// A gap between stacked tiles equal to the in-tile row gap, so the vertical
// rhythm is even across the tile seam. % of tile width → ≈ the in-tile gap once
// scaled by the tile aspect.
const TILE_GAP = "10%";

const slotStyles = passportSlot({ size: "fluid", rotation: "none" });

// Same playful, repeating tilt as the desktop book, keyed by global badge index.
const TILTS = [-1.2, 0.8, 1.1, -0.7, 0.6, -1, 1.2, -0.6, 0.9, -1.1, 0.7, -0.9];

export function PassportNotepad({ badges, onClose }: { badges: Badge[]; onClose: () => void }) {
  // Drop badges whose artwork isn't in the client bundle (mirrors the strip), then
  // group the survivors so section runs don't shift around a dropped badge.
  const resolved = filterBadgesWithArtwork(badges);

  // One tile per section page (six badges, a new section always starting a fresh
  // tile, title on the first tile of a run). Fall back to a single empty tile when
  // there are no badges so the notepad never collapses.
  const pages = paginateBySection(resolved);
  const tiles = pages.length > 0 ? pages : [null];

  // Hold the badges/titles hidden until the paper backdrop has decoded. The paper
  // is a CSS background (and the rings/bottom edges are `<img>`s), so we can't hang
  // a single `onLoad` off it — preload all three pieces and reveal together once
  // they're ready. Failures still release the gate, so a missing asset never leaves
  // the notepad blank. The fade is dropped under prefers-reduced-motion.
  const [paperReady, setPaperReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const decode = (src: string) => {
      const img = new Image();
      img.src = src;
      return img.decode().catch(() => undefined);
    };
    void Promise.all([passportRings, passportPaper, passportBottom].map(decode)).then(() => {
      if (!cancelled) setPaperReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    // Fill the sheet and scroll internally: the close stays pinned while the
    // notepad (rings -> paper tiles -> bottom edge) scrolls beneath it.
    <div className="relative mx-auto flex min-h-0 w-full max-w-[420px] flex-1 select-none flex-col">
      <PassportCloseButton onClose={onClose} className="top-3 right-3 size-11" />

      <div
        className={cx(
          "min-h-0 flex-1 overflow-y-auto motion-safe:transition-opacity motion-safe:duration-300",
          !paperReady && "pointer-events-none",
        )}
        style={{ opacity: paperReady ? 1 : 0 }}
      >
        <img src={passportRings} alt="" draggable={false} className="block w-full" />

        <div className="relative" style={{ backgroundImage: `url(${passportPaper})`, backgroundSize: "100% 100%" }}>
          <div className="mx-auto py-[2%]" style={{ width: INNER_WIDTH }}>
            {tiles.map((page, tileIndex) => (
              <div
                // Keyed by the tile's first badge (stable; tiles never reorder).
                key={page?.badges[0]?.id ?? `empty-notepad-${tileIndex}`}
                style={{ marginBottom: tileIndex < tiles.length - 1 ? TILE_GAP : 0 }}
              >
                {page?.showTitle && (
                  <CurvedSectionTitle section={page.section} variant="mobile" className="mb-[1%] w-full" />
                )}
                <div className="relative w-full" style={{ aspectRatio: `${TILE_W} / ${TILE_H}` }}>
                  {TILE_SLOTS.map((slot, slotIndex) => {
                    const badge = page?.badges[slotIndex];
                    const url = badge ? resolveBadgeImage(badge.image_key) : null;
                    if (badge && url) {
                      return (
                        <div
                          key={badge.id}
                          className="absolute"
                          style={{
                            left: `${slot.left}%`,
                            top: `${slot.top}%`,
                            width: `${slot.width}%`,
                            rotate: `${TILTS[(tileIndex * TILE_SLOTS.length + slotIndex) % TILTS.length]}deg`,
                          }}
                        >
                          <BadgeHoverCard badge={badge} triggerClassName={cx(slotStyles.slot(), "block")}>
                            <BadgeImage url={url} alt={badge.label} imageKey={badge.image_key} />
                          </BadgeHoverCard>
                        </div>
                      );
                    }
                    // Empty slot: its dashed arch, clipped out of the placeholder tile
                    // (the image fills the tile 1:1, so the slot box maps straight to
                    // the clip rect).
                    const heightPct = slot.width * (TILE_W / TILE_H);
                    return (
                      <img
                        key={`empty-${slot.left}-${slot.top}`}
                        src={passportPlaceholders}
                        alt=""
                        draggable={false}
                        className="absolute inset-0 size-full select-none"
                        style={{
                          clipPath: `inset(${slot.top}% ${100 - (slot.left + slot.width)}% ${100 - (slot.top + heightPct)}% ${slot.left}%)`,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <img src={passportBottom} alt="" draggable={false} className="block w-full" />
      </div>
    </div>
  );
}
